import React, { useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { GitBranch, Plus, Pencil, Trash2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import { message } from 'antd';
import {
  fetchSowDetails,
  addSowDetails,
  updateSowDetails,
  deleteSowDetails,
} from '../../redux/sowDetails/actions';
import './DPRSettingsPipelinesTab.css';
import {
  displayExpectedPipeLength,
  formatExpectedLength,
  sumProjectExpectedLength,
} from '../../utils/sowExpectedLength';
import { buildSowMeasurementApiFields } from '../../utils/sowApiFields';
import { NDT_PIPELINE_TYPE_OPTIONS } from '../../constants/ndtPipelineTypeOptions';

const PLANNED_WORK_OPTIONS = [
  { value: 'Components', label: 'Components' },
  { value: 'Days', label: 'Days' },
  { value: 'Field Joints', label: 'Field Joints' },
  { value: 'Welds', label: 'Welds' },
];

/** When production-line UI is hidden (coating), keep stations from the existing SOW row on save. */
function productionLinePayloadFromSowRecord(record, projectType) {
  if (!record || typeof record !== 'object') return {};
  const out = {};
  const sortedUniqueInts = (arr) =>
    [
      ...new Set(
        (Array.isArray(arr) ? arr : [])
          .map((x) => Number(x))
          .filter((n) => !Number.isNaN(n) && n > 0)
      ),
    ].sort((a, b) => a - b);

  if (projectType === 'Welding') {
    const w = sortedUniqueInts(record.weldingStations);
    if (w.length) out.weldingStations = w;
  } else if (projectType === 'Coating') {
    const c = sortedUniqueInts(record.coatingStations);
    if (c.length) out.coatingStations = c;
  } else if (projectType === 'Both') {
    const w = sortedUniqueInts(record.weldingStations);
    const c = sortedUniqueInts(record.coatingStations);
    if (w.length) out.weldingStations = w;
    if (c.length) out.coatingStations = c;
  }

  const ndtRaw = record.ndtStations;
  if (Array.isArray(ndtRaw) && ndtRaw.length > 0) {
    const ndtStations = ndtRaw
      .map((entry) => {
        const stationNumber = Number(
          typeof entry === 'object' && entry != null ? entry.stationNumber : entry
        );
        const ndtType = typeof entry === 'object' && entry != null ? entry.ndtType || '' : '';
        return { stationNumber, ndtType };
      })
      .filter((x) => !Number.isNaN(x.stationNumber) && x.stationNumber > 0);
    if (ndtStations.length) out.ndtStations = ndtStations;
  }
  return out;
}

const SimpleSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select',
  disabled = false,
  open,
  onOpenChange,
  containerRef,
}) => {
  const selected = options.find((o) => o.value === value);
  const displayLabel = selected ? selected.label : '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef?.current && !containerRef.current.contains(e.target))
        onOpenChange?.(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [containerRef, onOpenChange]);

  return (
    <div ref={containerRef} className="dpr-settings-pipelines-select-wrap">
      <div
        className="dpr-settings-pipelines-select-trigger"
        onClick={() => !disabled && onOpenChange?.(!open)}
        style={{
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span className="dpr-settings-pipelines-select-value">
          {displayLabel || placeholder}
        </span>
        {open ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
      </div>
      {open && (
        <div className="dpr-settings-pipelines-select-dropdown">
          {options.map((opt) => (
            <div
              key={opt.value}
              className="dpr-settings-pipelines-select-opt"
              onClick={() => {
                onChange(opt.value);
                onOpenChange(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DPRSettingsPipelinesTab = ({
  selectedProject,
  sowData,
  isSowLoading,
  projectTeamMembersAccessList,
  userAccessList,
  onFetchSowDetails,
  onAddSowDetails,
  onUpdateSowDetails,
  onDeleteSowDetails,
}) => {
  const [searchKey, setSearchKey] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [plannedWorkOpen, setPlannedWorkOpen] = useState(false);
  const plannedWorkRef = useRef(null);

  const [formPipelineSection, setFormPipelineSection] = useState('');
  const [formPipelineName, setFormPipelineName] = useState('');
  const [formPlannedStartDate, setFormPlannedStartDate] = useState('');
  const [formActualStartDate, setFormActualStartDate] = useState('');
  const [formPlannedEndDate, setFormPlannedEndDate] = useState('');
  const [formActualEndDate, setFormActualEndDate] = useState('');
  const [formOdInches, setFormOdInches] = useState('');
  const [formWallThicknessMm, setFormWallThicknessMm] = useState('');
  const [formPipeLength, setFormPipeLength] = useState('');
  const [formExpectedPipeLength, setFormExpectedPipeLength] = useState('');
  const [formPlannedWelds, setFormPlannedWelds] = useState('');
  const [formPlannedWorks, setFormPlannedWorks] = useState('');
  // Coating-related fields
  const [formMaterial, setFormMaterial] = useState('');
  const [formStalkId, setFormStalkId] = useState('');
  const [formFieldJointType, setFormFieldJointType] = useState('');
  const [formBlastStandard, setFormBlastStandard] = useState('');
  const [formPipeStringId, setFormPipeStringId] = useState('');
  const [formPipeStartDate, setFormPipeStartDate] = useState('');
  // Production Line Set-Up (per pipeline) — table-row model
  const [formStationRows, setFormStationRows] = useState([]);
  // row: { id, stationNumber: '' }
  const [formNdtType, setFormNdtType] = useState('');

  // Show coating fields only for Coating or Both project types
  const projectType = selectedProject?.projectType ?? '';
  const showCoatingFields = projectType === 'Coating' || projectType === 'Both';
  const showWeldingPipelineFields = projectType === 'Welding' || projectType === 'Both';
  const shouldAskNdtType = projectType === 'Welding' || projectType === 'Both';

  const pipelineData = Array.isArray(sowData) ? sowData : [];
  const projectExpectedLength = useMemo(
    () => sumProjectExpectedLength(pipelineData),
    [pipelineData]
  );
  const list = (() => {
    const key = searchKey.trim().toLowerCase();
    if (!key) return pipelineData;
    return pipelineData.filter((item) =>
      String(item?.pipelineSection ?? '').toLowerCase().includes(key)
    );
  })();

  const isWriteAccess = (() => {
    const groups =
      userAccessList?.length > 0
        ? userAccessList
        : (() => {
          try {
            return JSON.parse(localStorage.getItem('UserGroup') || '[]');
          } catch (e) {
            return [];
          }
        })();
    if (Array.isArray(groups) && (groups.includes('admin') || groups.includes('super-admin')))
      return true;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return false;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !projectTeamMembersAccessList?.[0]?.tabPermissions?.Details?.write
    )
      return false;
    return true;
  })();

  useEffect(() => {
    if (selectedProject?.id) {
      onFetchSowDetails(selectedProject.id);
    }
  }, [selectedProject?.id]);

  const resetForm = () => {
    setFormPipelineSection('');
    setFormPipelineName('');
    setFormPlannedStartDate('');
    setFormActualStartDate('');
    setFormPlannedEndDate('');
    setFormActualEndDate('');
    setFormOdInches('');
    setFormWallThicknessMm('');
    setFormPipeLength('');
    setFormExpectedPipeLength('');
    setFormPlannedWelds('');
    setFormPlannedWorks('');
    setFormMaterial('');
    setFormStalkId('');
    setFormFieldJointType('');
    setFormBlastStandard('');
    setFormPipeStringId('');
    setFormPipeStartDate('');
    setFormStationRows([]);
    setFormNdtType('');
    setEditRecord(null);
  };

  const openAddModal = () => {
    if (!selectedProject?.id) {
      message.warning('Select a project to add pipeline.');
      return;
    }
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    setFormPipelineSection(record?.pipelineSection ?? '');
    setFormPipelineName(record?.pipelineName ?? '');
    setFormPlannedStartDate(record?.plannedStartDate ? String(record.plannedStartDate).slice(0, 10) : '');
    setFormActualStartDate(record?.actualStartDate ? String(record.actualStartDate).slice(0, 10) : '');
    setFormPlannedEndDate(record?.plannedEndDate ? String(record.plannedEndDate).slice(0, 10) : '');
    setFormActualEndDate(record?.actualEndDate ? String(record.actualEndDate).slice(0, 10) : '');
    setFormOdInches(
      record?.odInches != null || record?.outerDiameterInch != null
        ? String(record.odInches ?? record.outerDiameterInch)
        : ''
    );
    setFormWallThicknessMm(record?.wallThicknessMm != null ? String(record.wallThicknessMm) : '');
    setFormPipeLength(record?.pipeLength != null ? String(record.pipeLength) : '');
    setFormExpectedPipeLength(record?.expectedPipeLength != null ? String(record.expectedPipeLength) : '');
    setFormPlannedWelds(String(record?.plannedWelds ?? ''));
    setFormPlannedWorks(record?.plannedWorks ?? '');
    setFormMaterial(record?.material ?? '');
    setFormStalkId(record?.stalkId ?? '');
    setFormFieldJointType(record?.fieldJointType ?? '');
    setFormBlastStandard(record?.blastStandard ?? '');
    setFormPipeStringId(record?.pipeStringId ?? '');
    setFormPipeStartDate(record?.pipeStartDate ? String(record.pipeStartDate).slice(0, 10) : '');
    // Restore station numbers (union of welding / coating / NDT); global NDT type from first NDT entry
    const nums = new Set();
    if (Array.isArray(record?.weldingStations)) {
      record.weldingStations.forEach((n) => {
        const v = Number(n);
        if (!isNaN(v) && v > 0) nums.add(v);
      });
    }
    if (Array.isArray(record?.coatingStations)) {
      record.coatingStations.forEach((n) => {
        const v = Number(n);
        if (!isNaN(v) && v > 0) nums.add(v);
      });
    }
    if (Array.isArray(record?.ndtStations)) {
      record.ndtStations.forEach((s) => {
        const raw = typeof s === 'object' && s != null ? s.stationNumber : s;
        const v = Number(raw);
        if (!isNaN(v) && v > 0) nums.add(v);
      });
    }
    const sortedNums = [...nums].sort((a, b) => a - b);
    const restoredRows = sortedNums.map((n, idx) => ({
      id: `${Date.now()}-${idx}-${Math.random()}`,
      stationNumber: String(n),
    }));
    const firstNdtType =
      Array.isArray(record?.ndtStations) && record.ndtStations.length > 0
        ? (record.ndtStations[0]?.ndtType ?? '')
        : '';
    if (showWeldingPipelineFields) {
      setFormStationRows(restoredRows);
      setFormNdtType(firstNdtType || '');
    } else {
      setFormStationRows([]);
      setFormNdtType('');
    }
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const isPipeStringIdUnique = (pipeStringId, excludeId) => {
    const trimmed = (pipeStringId || '').trim();
    if (!trimmed) return false;
    return !pipelineData.some(
      (item) => item.pipeStringId === trimmed && item.id !== excludeId
    );
  };

  const handleSave = () => {
    const pipelineSection = (formPipelineSection || '').trim();

    if (!pipelineSection) {
      message.warning('Pipeline Number is required.');
      return;
    }

    const pipeStringId = (formPipeStringId || '').trim();
    if (pipeStringId && !isPipeStringIdUnique(pipeStringId, editRecord?.id)) {
      message.warning('Pipe String ID is already in use. Try another.');
      return;
    }

    const trimU = (v) => {
      if (v === undefined || v === null) return undefined;
      const t = String(v).trim();
      return t === '' ? undefined : t;
    };

    const pipelineName = trimU(formPipelineName);
    const plannedStartDate = trimU(formPlannedStartDate);
    const actualStartDate = trimU(formActualStartDate);
    const plannedEndDate = trimU(formPlannedEndDate);
    const actualEndDate = trimU(formActualEndDate);
    const plannedWelds = showWeldingPipelineFields
      ? formPlannedWelds === ''
        ? ''
        : String(formPlannedWelds).trim()
      : '';
    const plannedWorks = trimU(formPlannedWorks);
    const pipeLength = showWeldingPipelineFields
      ? trimU(formPipeLength)
      : editRecord != null
        ? trimU(editRecord.pipeLength)
        : undefined;
    const pipeStartDate = trimU(formPipeStartDate);
    const material = trimU(formMaterial);
    const stalkId = trimU(formStalkId);
    const fieldJointType = trimU(formFieldJointType);
    const blastStandard = trimU(formBlastStandard);

    const measurement = showWeldingPipelineFields
      ? buildSowMeasurementApiFields(formOdInches, formWallThicknessMm, formExpectedPipeLength)
      : {};

    const plannedWeldsForApi = showWeldingPipelineFields
      ? plannedWelds === ''
        ? 0
        : Number(plannedWelds)
      : editRecord != null
        ? Number(editRecord.plannedWelds ?? 0)
        : 0;

    let productionLinePayload = {};
    if (showWeldingPipelineFields) {
      const stationNums = [
        ...new Set(
          formStationRows
            .map((r) => Number(r.stationNumber))
            .filter((n) => !isNaN(n) && n > 0)
        ),
      ].sort((a, b) => a - b);

      if (stationNums.length > 0) {
        if (projectType === 'Welding') {
          productionLinePayload.weldingStations = stationNums;
          if (shouldAskNdtType && formNdtType) {
            productionLinePayload.ndtStations = stationNums.map((stationNumber) => ({
              stationNumber,
              ndtType: formNdtType,
            }));
          }
        } else if (projectType === 'Coating') {
          productionLinePayload.coatingStations = stationNums;
        } else if (projectType === 'Both') {
          productionLinePayload.weldingStations = stationNums;
          productionLinePayload.coatingStations = stationNums;
          if (shouldAskNdtType && formNdtType) {
            productionLinePayload.ndtStations = stationNums.map((stationNumber) => ({
              stationNumber,
              ndtType: formNdtType,
            }));
          }
        }
      }
    } else if (editRecord) {
      productionLinePayload = productionLinePayloadFromSowRecord(editRecord, projectType);
    }

    if (editRecord) {
      onUpdateSowDetails({
        data: {
          id: editRecord.id,
          projectId: editRecord.projectId ?? selectedProject.id,
          pipelineSection,
          pipelineName,
          plannedStartDate,
          actualStartDate,
          plannedEndDate,
          actualEndDate,
          material,
          plannedWelds: plannedWeldsForApi,
          plannedWorks,
          stalkId,
          fieldJointType,
          blastStandard,
          pipeStringId: pipeStringId || undefined,
          pipeLength,
          pipeStartDate,
          ...measurement,
          ...productionLinePayload,
        },
      });
      closeAddModal();
    } else {
      onAddSowDetails({
        projectId: selectedProject.id,
        pipelineSection,
        pipelineName,
        plannedStartDate,
        actualStartDate,
        plannedEndDate,
        actualEndDate,
        material,
        plannedWelds: plannedWeldsForApi,
        plannedWorks,
        stalkId,
        fieldJointType,
        blastStandard,
        pipeStringId: pipeStringId || undefined,
        totalWeldsCount: 0,
        pipeLength,
        pipeStartDate,
        ...measurement,
        ...productionLinePayload,
      });
      closeAddModal();
    }
  };

  const addFormRow = () =>
    setFormStationRows((prev) => [...prev, { id: Date.now() + Math.random(), stationNumber: '' }]);

  const removeFormRow = (id) =>
    setFormStationRows((prev) => prev.filter((r) => r.id !== id));

  const updateFormRow = (id, key, value) =>
    setFormStationRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));

  const showDeleteModal = (record) => {
    setRecordToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!recordToDelete) return;
    onDeleteSowDetails({ data: recordToDelete });
    setIsDeleteModalOpen(false);
    setRecordToDelete(null);
  };

  if (!selectedProject) {
    return (
      <div className="dpr-settings-pipelines-tab">
        <div className="dpr-settings-pipelines-card">
          <p style={{ margin: 0, color: '#64748B' }}>
            Select a project to manage pipelines.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dpr-settings-pipelines-tab">
      <div className="dpr-settings-pipelines-layout">
        <div className="dpr-settings-pipelines-main">
          <div className="dpr-settings-pipelines-card">
            <div className="dpr-settings-pipelines-header">
              <div className="dpr-settings-pipelines-header-left">
                <div className="dpr-settings-pipelines-icon-wrap">
                  <GitBranch size={24} />
                </div>
                <div className="dpr-settings-pipelines-header-content">
                  <div className="dpr-settings-pipelines-title-row">
                    <h3 className="dpr-settings-pipelines-title">Pipelines</h3>
                    <span className="dpr-settings-pipelines-summary">
                      {list.length} pipeline{list.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="dpr-settings-pipelines-description">
                    Manage pipeline sections (Statement of Work): add, edit, delete.
                  </p>
                </div>
              </div>
              <div className="dpr-settings-pipelines-actions">
                <input
                  type="text"
                  className="dpr-settings-pipelines-search"
                  placeholder="Search By Pipeline Section"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  aria-label="Search By Pipeline Section"
                />
                <div className="dpr-settings-pipelines-project-expected" title="Sum of planned qty × pipe length per mainline">
                  <span className="dpr-settings-pipelines-project-expected-label">Project expected length</span>
                  <span className="dpr-settings-pipelines-project-expected-value">
                    {formatExpectedLength(projectExpectedLength)}
                  </span>
                </div>
                <Button
                  label="Add New"
                  variant="primary"
                  size="small"
                  icon={Plus}
                  onClick={openAddModal}
                  disabled={!isWriteAccess}
                />
              </div>
            </div>

            <div className="dpr-settings-pipelines-table-wrap">
              <div className="dpr-settings-pipelines-thead">
                <div className="dpr-settings-pipelines-header-row">
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-num">#</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-section">PIPELINE SECTION</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-id">PIPE STRING ID</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">MATERIAL</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">STALK #</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">FIELD JOINT TYPE</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">BLAST STANDARD</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">PLANNED WORKS</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">PLANNED QTY</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">OD (IN.)</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">WT (MM)</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">PIPE LENGTH</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-expected">EXPECTED PIPE LENGTH</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-date">PIPE START</div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-actions">ACTIONS</div>
                </div>
              </div>

              {isSowLoading ? (
                <div className="dpr-settings-pipelines-loading">
                  <Spinner size="medium" />
                </div>
              ) : list.length === 0 ? (
                <div className="dpr-settings-pipelines-empty">
                  {searchKey.trim() ? (
                    <>No matching pipelines for &quot;{searchKey.trim()}&quot;.</>
                  ) : (
                    <>No pipelines yet. Click &quot;Add New&quot; to add a pipeline section.</>
                  )}
                </div>
              ) : (
                <div className="dpr-settings-pipelines-rows">
                  {list.map((row, index) => (
                    <div key={row.id} className="dpr-settings-pipelines-row">
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-num">
                        {index + 1}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-section">
                        {row.pipelineSection ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-id">
                        {row.pipeStringId ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.material ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.stalkId ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.fieldJointType ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.blastStandard ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.plannedWorks ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.plannedWelds ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.odInches ?? row.outerDiameterInch ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.wallThicknessMm ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.pipeLength ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-expected">
                        {displayExpectedPipeLength(row)}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-date">
                        {row.pipeStartDate
                          ? String(row.pipeStartDate).slice(0, 10)
                          : '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-actions">
                        <button
                          type="button"
                          className="dpr-settings-pipelines-action-btn"
                          onClick={() => openEditModal(row)}
                          disabled={!isWriteAccess}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          type="button"
                          className="dpr-settings-pipelines-action-btn dpr-settings-pipelines-action-delete"
                          onClick={() => showDeleteModal(row)}
                          disabled={!isWriteAccess}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div
          className="dpr-settings-pipelines-modal-backdrop"
          onClick={closeAddModal}
        >
          <div
            className="dpr-settings-pipelines-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dpr-settings-pipelines-modal-head">
              <h3 className="dpr-settings-pipelines-modal-title">
                {editRecord ? 'Edit Pipeline Section' : 'Add Pipeline Section'}
              </h3>
              <button
                type="button"
                className="dpr-settings-pipelines-modal-close"
                onClick={closeAddModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="dpr-settings-pipelines-modal-body">
              {/* ── Mandatory pipeline information ── */}
              <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                <div className="dpr-settings-pipelines-field">
                  <label>Pipeline Number <span className="required">*</span></label>
                  <input
                    type="text"
                    className="dpr-settings-pipelines-input"
                    placeholder="e.g. Bundle 3.1"
                    value={formPipelineSection}
                    onChange={(e) => setFormPipelineSection(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Pipeline Name</label>
                  <input
                    type="text"
                    className="dpr-settings-pipelines-input"
                    placeholder="Pipeline name"
                    value={formPipelineName}
                    onChange={(e) => setFormPipelineName(e.target.value)}
                  />
                </div>
              </div>
              <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                <div className="dpr-settings-pipelines-field">
                  <label>Planned Start Date</label>
                  <input
                    type="date"
                    className="dpr-settings-pipelines-input"
                    value={formPlannedStartDate}
                    onChange={(e) => setFormPlannedStartDate(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Actual Start Date</label>
                  <input
                    type="date"
                    className="dpr-settings-pipelines-input"
                    value={formActualStartDate}
                    onChange={(e) => setFormActualStartDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                <div className="dpr-settings-pipelines-field">
                  <label>Planned End Date</label>
                  <input
                    type="date"
                    className="dpr-settings-pipelines-input"
                    value={formPlannedEndDate}
                    onChange={(e) => setFormPlannedEndDate(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Actual End Date</label>
                  <input
                    type="date"
                    className="dpr-settings-pipelines-input"
                    value={formActualEndDate}
                    onChange={(e) => setFormActualEndDate(e.target.value)}
                  />
                </div>
              </div>
              {showWeldingPipelineFields && (
                <>
                  <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                    <div className="dpr-settings-pipelines-field">
                      <label>Pipeline OD (inches)</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="e.g. 16"
                        value={formOdInches}
                        onChange={(e) => setFormOdInches(e.target.value)}
                      />
                    </div>
                    <div className="dpr-settings-pipelines-field">
                      <label>Pipeline WT (mm)</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="e.g. 12.7"
                        value={formWallThicknessMm}
                        onChange={(e) => setFormWallThicknessMm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                    <div className="dpr-settings-pipelines-field">
                      <label>Single Pipe Length</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="e.g. 12.2 m"
                        value={formPipeLength}
                        onChange={(e) => setFormPipeLength(e.target.value)}
                      />
                    </div>
                    <div className="dpr-settings-pipelines-field">
                      <label>Pipeline Planned Length</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="e.g. 1250 m"
                        value={formExpectedPipeLength}
                        onChange={(e) => setFormExpectedPipeLength(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                {showWeldingPipelineFields && (
                  <div className="dpr-settings-pipelines-field">
                    <label>Pipeline Planned Welds</label>
                    <input
                      type="number"
                      className="dpr-settings-pipelines-input"
                      placeholder="e.g. 450"
                      value={formPlannedWelds}
                      onChange={(e) => setFormPlannedWelds(e.target.value)}
                      min={0}
                    />
                  </div>
                )}
                <div className="dpr-settings-pipelines-field">
                  <label>Planned Work Type</label>
                  <SimpleSelect
                    value={formPlannedWorks}
                    options={PLANNED_WORK_OPTIONS}
                    onChange={setFormPlannedWorks}
                    placeholder="Select planned work type"
                    open={plannedWorkOpen}
                    onOpenChange={setPlannedWorkOpen}
                    containerRef={plannedWorkRef}
                  />
                </div>
              </div>

              {/* ── Coating fields: only shown for Coating / Both project types ── */}
              {showCoatingFields && (
                <div className="dpr-settings-pipelines-advanced-section">
                  <div className="dpr-settings-pipelines-section-divider">
                    <span className="dpr-settings-pipelines-section-label">Coating information</span>
                  </div>
                  <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                    <div className="dpr-settings-pipelines-field">
                      <label>Material</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="Material"
                        value={formMaterial}
                        onChange={(e) => setFormMaterial(e.target.value)}
                      />
                    </div>
                    <div className="dpr-settings-pipelines-field">
                      <label>Stalk #</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="Stalk #"
                        value={formStalkId}
                        onChange={(e) => setFormStalkId(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                    <div className="dpr-settings-pipelines-field">
                      <label>Field Joint Type</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="Field Joint Type"
                        value={formFieldJointType}
                        onChange={(e) => setFormFieldJointType(e.target.value)}
                      />
                    </div>
                    <div className="dpr-settings-pipelines-field">
                      <label>Blast Standard</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="Blast Standard"
                        value={formBlastStandard}
                        onChange={(e) => setFormBlastStandard(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="dpr-settings-pipelines-form-row dpr-settings-pipelines-form-row-split">
                    <div className="dpr-settings-pipelines-field">
                      <label>Pipe String ID</label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="Pipe String ID"
                        value={formPipeStringId}
                        onChange={(e) => setFormPipeStringId(e.target.value)}
                      />
                    </div>
                    <div className="dpr-settings-pipelines-field">
                      <label>Pipe Start Date</label>
                      <input
                        type="date"
                        className="dpr-settings-pipelines-input"
                        value={formPipeStartDate}
                        onChange={(e) => setFormPipeStartDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Production Line Set-Up (welding / both only) ── */}
              {showWeldingPipelineFields && (
              <>
              <div className="dpr-settings-pipelines-section-divider">
                <span className="dpr-settings-pipelines-section-label">Production Line Set-Up</span>
              </div>

              {/* Table header */}
              {shouldAskNdtType && (
                <div style={{ margin: '6px 0 10px 0' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                    NDT Type (applies to all stations below)
                  </div>
                  <select
                    value={formNdtType}
                    onChange={(e) => setFormNdtType(e.target.value)}
                    className="dpr-settings-pipelines-input"
                    style={{ minWidth: 'min(100%, 320px)', maxWidth: '100%', cursor: 'pointer' }}
                  >
                    <option value="">Select NDT Type</option>
                    {NDT_PIPELINE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formStationRows.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 32px',
                  gap: '8px',
                  padding: '4px 2px 8px 2px',
                  borderBottom: '1px solid #E2E8F0',
                  marginBottom: '4px',
                }}>
                  {['St. No.', ''].map((h, idx) => (
                    <span key={h || `h-${idx}`} style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</span>
                  ))}
                </div>
              )}

              {/* Station rows */}
              {formStationRows.map((row) => (
                <div
                  key={row.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '100px 32px',
                    gap: '8px',
                    alignItems: 'center',
                    marginBottom: '6px',
                  }}
                >
                  {/* Station number — editable */}
                  <input
                    type="number"
                    min={1}
                    className="dpr-settings-pipelines-input"
                    placeholder="No."
                    value={row.stationNumber}
                    onChange={(e) => updateFormRow(row.id, 'stationNumber', e.target.value)}
                    style={{ width: '100%' }}
                  />

                  {/* Delete row */}
                  <button
                    type="button"
                    onClick={() => removeFormRow(row.id)}
                    style={{
                      width: '28px', height: '28px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: '#FEF2F2',
                      border: '1px solid #FECACA',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      color: '#EF4444',
                      fontSize: '16px',
                      lineHeight: 1,
                      padding: 0,
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}

              {/* + Add row */}
              <button
                type="button"
                onClick={addFormRow}
                style={{
                  marginTop: '6px',
                  marginBottom: '4px',
                  padding: '6px 16px',
                  background: '#ef314c',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                + Add row
              </button>

              <p className="dpr-settings-pipelines-modal-hint">
                *Weld number will generate as shown in example: WeldName-Weld-01
              </p>
              </>
              )}
            </div>
            <div className="dpr-settings-pipelines-modal-foot">
              <Button label="Cancel" variant="tertiary" size="medium" onClick={closeAddModal} />
              <Button
                label={editRecord ? 'Update' : 'Add'}
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={!(formPipelineSection || '').trim()}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        title="Delete Pipeline Section"
        message={
          recordToDelete ? (
            <>
              Deleting this pipeline section will delete all corresponding Stations and related information.
              Are you sure you want to delete{' '}
              <strong style={{ color: '#EF4444' }}>{recordToDelete.pipelineSection}</strong>?
            </>
          ) : (
            'Are you sure you want to delete this pipeline section?'
          )
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
        }}
      />
    </div>
  );
};

const mapStateToProps = ({ sow, equip }) => ({
  sowData: get(sow, 'sowData', []),
  isSowLoading: get(sow, 'isSowLoading', false),
  projectTeamMembersAccessList: get(equip, 'projectTeamMembersAccessList', []),
  userAccessList: get(equip, 'userAccessList', []),
});

const mapDispatchToProps = {
  onFetchSowDetails: fetchSowDetails,
  onAddSowDetails: addSowDetails,
  onUpdateSowDetails: updateSowDetails,
  onDeleteSowDetails: deleteSowDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(DPRSettingsPipelinesTab);
